import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useProductSearch } from '../hooks/useProductSearch';
import { usePosCart } from '../hooks/usePosCart';
import { formatCurrency } from '../utils/posCalculations';

export const ProductSearch: React.FC = () => {
  const {
    query,
    results,
    loading,
    categories,
    selectedCategory,
    setSelectedCategory,
    setQuery,
  } = useProductSearch();
  const { addToCart, saving } = usePosCart();

  const handleAddProduct = async (product: any) => {
    await addToCart({
      idArticulo: product.idArticulo,
      descripcion: product.descripcion,
      unidadMedida: product.unidadDeMedida || 'UND',
      cantidad: 1,
      precioUnitario: product.precioVenta || 0,
    });
  };

  const handleCategoryClick = (cat: string) => {
    setSelectedCategory(selectedCategory === cat ? null : cat);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TextField
        fullWidth
        placeholder="Buscar producto por nombre o codigo..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        size="small"
        sx={{ mb: 0.5 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {/* Category chips - horizontal scroll */}
      {categories.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            mb: 0.5,
            py: 0.5,
            overflowX: 'auto',
            flexShrink: 0,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 2 },
          }}
        >
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              size="small"
              color={selectedCategory === cat ? 'primary' : 'default'}
              variant={selectedCategory === cat ? 'filled' : 'outlined'}
              onClick={() => handleCategoryClick(cat)}
              sx={{ flexShrink: 0, fontSize: '0.7rem', height: 26 }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Grid container spacing={1}>
          {results.map((product) => (
            <Grid key={product.idArticulo} size={{ xs: 6, sm: 4, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea
                  onClick={() => handleAddProduct(product)}
                  disabled={saving}
                  sx={{ height: '100%', p: 1 }}
                >
                  <CardContent sx={{ p: '4px !important' }}>
                    <Typography variant="body2" noWrap fontWeight="bold">
                      {product.descripcion}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {product.codigoArticulo}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {formatCurrency(product.precioVenta || 0)}
                      </Typography>
                      <Chip
                        icon={<CartIcon />}
                        label="Agregar"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 24 }}
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {!loading && results.length === 0 && (query.length >= 2 || selectedCategory) && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No se encontraron productos</Typography>
          </Box>
        )}

        {!selectedCategory && !query && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography color="text.secondary">
              Seleccione una categoria o busque un producto
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProductSearch;
